/**
 * Breaking one hand's score into the lines the count-up walks (DESIGN-client §3.7 `handScored`:
 * "sequential lines contract → bags → nils → penalty → boston").
 *
 * The cue gives the authoritative `delta` and the parts that produced it (contract, taken,
 * bagsAdded, nilResults, bagPenalty, boston) but not each part's point value, because that
 * depends on rules the client is not asked to re-derive (`tenFor200`, `blindNilDeficit`,
 * `failedNilTricks`).
 *
 * So: name every line we can, price it at the standard rule, and put whatever is left over onto
 * the contract line. The displayed lines then always sum to the server's `delta` exactly. The
 * total is authoritative; the breakdown is presentation.
 */
import type { CueData } from '../cues/cueTypes';

export type TeamHandScore = CueData['handScored']['teams'][number];

export type ScoreLineKind = 'contract' | 'bags' | 'nil' | 'penalty' | 'boston';

export interface ScoreLine {
  kind: ScoreLineKind;
  /** Short label for a HUD or a log; the scene itself only animates the number. */
  label: string;
  delta: number;
}

const NIL_POINTS = 100;
const BLIND_NIL_POINTS = 200;
const BAG_PENALTY = -100;
const BOSTON_BONUS = 200;

/**
 * The lines for one team, in the order §3.7 walks them. Sums to `team.delta`.
 * A team with nothing to say (no contract, no bags, no nils) gets one contract line of 0 so the
 * walk still has a beat to land on.
 */
export function scoreLines(team: TeamHandScore): ScoreLine[] {
  const lines: ScoreLine[] = [];

  const contract: ScoreLine = {
    kind: 'contract',
    label: team.made ? `Made ${team.contract}` : `Set on ${team.contract}`,
    delta: team.made ? team.contract * 10 : -team.contract * 10,
  };
  lines.push(contract);

  if (team.bagsAdded > 0) {
    lines.push({ kind: 'bags', label: `+${team.bagsAdded} bag${team.bagsAdded === 1 ? '' : 's'}`, delta: team.bagsAdded });
  }

  for (const nil of team.nilResults) {
    const points = nil.blind ? BLIND_NIL_POINTS : NIL_POINTS;
    lines.push({
      kind: 'nil',
      label: `${nil.blind ? 'Blind nil' : 'Nil'} ${nil.success ? 'made' : 'broken'}`,
      delta: nil.success ? points : -points,
    });
  }

  if (team.bagPenalty) lines.push({ kind: 'penalty', label: 'Ten bags', delta: BAG_PENALTY });
  if (team.boston) lines.push({ kind: 'boston', label: 'Boston', delta: BOSTON_BONUS });

  // Reconcile: whatever the rules did that we did not price goes onto the contract line, so the
  // walk always lands exactly on the server's total.
  const named = lines.reduce((n, l) => n + l.delta, 0);
  contract.delta += team.delta - named;
  return lines;
}

/** Running totals after each line, starting from `from`. Ends at `from + team.delta`. */
export function runningTotals(from: number, lines: readonly ScoreLine[]): number[] {
  let acc = from;
  return lines.map((l) => (acc += l.delta));
}
